import { CdkDragEnd, CdkDragMove, CdkDragStart } from '@angular/cdk/drag-drop';
import { Component, OnInit, Input, Output, EventEmitter, ElementRef, ViewChild, OnChanges, signal, computed } from '@angular/core';
import { AvailableFlowObjectType, FlowObjectDefinition, FlowObjectType, FlowObjectTypeClassName } from '../../../models/flow-object.model';
import { PopoverComponent } from '../../popover/popover.component';
import { IMPORTS } from './_imports';

@Component({
    selector: 'available-flow-object',
    templateUrl: './available-flow-object.component.html',
    styleUrl: './available-flow-object.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class AvailableFlowObjectComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('contextModeRef') contextModeRef: ElementRef<HTMLElement>;
    @ViewChild('flowObjectBodyContainerRef') flowObjectBodyContainerRef: ElementRef<HTMLElement>;
    @ViewChild('actionsMatPopoverRef') actionsMatPopoverRef: PopoverComponent;
    // #endregion

    // #region [Type props]
    FlowObjectType = FlowObjectType;
    AvailableFlowObjectType = AvailableFlowObjectType;
    FlowObjectTypeClassName = FlowObjectTypeClassName;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectDefinition);
    $isReadOnlyMode = signal(false);
    $hasGatewayPath = signal(false);
    $hasFlowStarter = signal(false);
    $firstGatewayPathName = signal(null as string);
    $secondGatewayPathName = signal(null as string);
    // #endregion

    // #region [computeds]
    $isUniqueTaskDisabled = computed(() =>
        this.$hasGatewayPath() && this.$model().typeId == FlowObjectType.GatewayPaths
        || this.$hasFlowStarter() && this.$isStartingTask()
    );
    $isStartingTask = computed(() =>
        [
            FlowObjectType.StartForm,
            FlowObjectType.StartInboundApi
        ].includes(this.$model().typeId)
    );
    $isFinishingTask = computed(() =>
        [
            FlowObjectType.Finish,
            FlowObjectType.FinishOutboundApi,
            FlowObjectType.FinishContent
        ].includes(this.$model().typeId)
    );
    $isGatewayTask = computed(() =>
        [
            FlowObjectType.GatewayApprove,
            FlowObjectType.GatewayRules,
            FlowObjectType.GatewayPaths
        ].includes(this.$model().typeId)
    );
    $availableTaskType = computed(() => {
        if (this.$isStartingTask()) {
            return AvailableFlowObjectType.Starting;
        }

        if (this.$isFinishingTask()) {
            return AvailableFlowObjectType.Finishing;
        }

        if (this.$isGatewayTask()) {
            return AvailableFlowObjectType.Gateway;
        }

        return AvailableFlowObjectType.General;
    });
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Input() _hasGatewayPath: boolean;
    @Input() _hasFlowStarter: boolean;
    @Input() _firstGatewayPathName: string;
    @Input() _secondGatewayPathName: string;
    @Output() _selectFlowObjectEvent = new EventEmitter<{ flowObject: FlowObjectDefinition, isFirstGatewayPath: boolean, isSecondGatewayPath: boolean }>();
    @Output() _dragStartedEvent = new EventEmitter<{ event: CdkDragStart, isAvailableFlowObject: boolean }>();
    @Output() _dragEndedEvent = new EventEmitter<CdkDragEnd>();
    @Output() _dragMovedEvent = new EventEmitter<CdkDragMove>();
    // #endregion

    constructor(public element: ElementRef) { }

    ngOnInit() { }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
        this.$hasGatewayPath.set(this._hasGatewayPath);
        this.$hasFlowStarter.set(this._hasFlowStarter);
        this.$firstGatewayPathName.set(this._firstGatewayPathName);
        this.$secondGatewayPathName.set(this._secondGatewayPathName);
    }

    isOverflown(elem: HTMLElement): boolean {
        let flowObjectBodyContainer = this.flowObjectBodyContainerRef?.nativeElement;
        if (flowObjectBodyContainer == null) return false;

        // compensação por um fator de ajuste empírico
        return flowObjectBodyContainer.scrollWidth > elem.clientWidth * .87;
    }

    selectFlowObject(isFirstGatewayPath = false, isSecondGatewayPath = false) {
        this._selectFlowObjectEvent.emit({
            flowObject: this.$model(),
            isFirstGatewayPath: isFirstGatewayPath,
            isSecondGatewayPath: isSecondGatewayPath
        });

        this.actionsMatPopoverRef.close();
    }

    dragStarted(event: CdkDragStart, isAvailableFlowObject = false) {
        this._dragStartedEvent.emit({ event, isAvailableFlowObject });
    }

    dragEnded(event: CdkDragEnd) {
        this._dragEndedEvent.emit(event);
    }

    dragMoved(event: CdkDragMove) {
        this._dragMovedEvent.emit(event);
    }

    clickEvent() {
        if (this.$isUniqueTaskDisabled()) return;

        if (this.$hasGatewayPath()) {
            this.actionsMatPopoverRef.open();
        } else {
            this.selectFlowObject();
        }
    }

    // #region [===== private methods =====]
    // #endregion
}
